/** TanStack Query mutation hooks for draft structural editing. */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { courseVersionKeys } from './useCourseVersions';
import { toast } from 'sonner';
import { extractErrorMessage } from '@/shared/utils/extractErrorMessage';
import type { CourseVersionWithStructure } from '@formency/shared';

function base(courseId: string, versionId: string) {
  return `/courses/${courseId}/versions/${versionId}`;
}

function useInvalidateDraft(courseId: string) {
  const qc = useQueryClient();
  return (data: CourseVersionWithStructure) => {
    qc.setQueryData(courseVersionKeys.active(courseId), data);
    qc.invalidateQueries({ queryKey: courseVersionKeys.detail(courseId, data.id) });
  };
}

// ─── Optimistic update helpers ──────────────────────────────────────────────

type DraftSnapshot = { prev: CourseVersionWithStructure | undefined };

async function snapshotDraft(
  qc: ReturnType<typeof useQueryClient>,
  courseId: string
): Promise<DraftSnapshot> {
  await qc.cancelQueries({ queryKey: courseVersionKeys.active(courseId) });
  return { prev: qc.getQueryData<CourseVersionWithStructure>(courseVersionKeys.active(courseId)) };
}

function rollbackDraft(
  qc: ReturnType<typeof useQueryClient>,
  courseId: string,
  context: DraftSnapshot | undefined
) {
  if (context?.prev) {
    qc.setQueryData(courseVersionKeys.active(courseId), context.prev);
  }
}

function settleDraft(qc: ReturnType<typeof useQueryClient>, courseId: string) {
  qc.invalidateQueries({ queryKey: courseVersionKeys.active(courseId) });
}

type DraftModule = CourseVersionWithStructure['modules'][number];
type DraftTopic = DraftModule['topics'][number];
type DraftLesson = DraftTopic['lessons'][number];

/** Reorder an array to match the given ID order. */
function reorderByIds<T extends { id: string }>(items: T[], orderedIds: string[]): T[] {
  const map = new Map(items.map((item) => [item.id, item]));
  return orderedIds.map((id) => map.get(id)).filter((item): item is T => item !== undefined);
}

/** Extract a topic from the module tree. Returns [topic, updatedModules] or null. */
function extractTopic(modules: DraftModule[], topicId: string): [DraftTopic, DraftModule[]] | null {
  let found: DraftTopic | undefined;
  const updated = modules.map((mod) => {
    const idx = mod.topics.findIndex((t) => t.id === topicId);
    if (idx === -1) return mod;
    found = mod.topics[idx];
    return { ...mod, topics: mod.topics.filter((t) => t.id !== topicId) };
  });
  return found ? [found, updated] : null;
}

/** Extract a lesson from the module→topic tree. Returns [lesson, updatedModules] or null. */
function extractLesson(
  modules: DraftModule[],
  lessonId: string
): [DraftLesson, DraftModule[]] | null {
  let found: DraftLesson | undefined;
  const updated = modules.map((mod) => ({
    ...mod,
    topics: mod.topics.map((topic) => {
      const idx = topic.lessons.findIndex((l) => l.id === lessonId);
      if (idx === -1) return topic;
      found = topic.lessons[idx];
      return { ...topic, lessons: topic.lessons.filter((l) => l.id !== lessonId) };
    }),
  }));
  return found ? [found, updated] : null;
}

// ─── Modules ────────────────────────────────────────────────────────────────

export function useAddDraftModule(courseId: string, versionId: string) {
  const onDraftUpdate = useInvalidateDraft(courseId);
  return useMutation({
    mutationFn: (data: { title: string; description?: string }) =>
      api.post<CourseVersionWithStructure>(`${base(courseId, versionId)}/modules`, data),
    onSuccess: onDraftUpdate,
    onError: (e: unknown) => toast.error(extractErrorMessage(e, 'Failed to add module')),
  });
}

export function useUpdateDraftModule(courseId: string, versionId: string) {
  const onDraftUpdate = useInvalidateDraft(courseId);
  return useMutation({
    mutationFn: ({
      moduleId,
      data,
    }: {
      moduleId: string;
      data: { title?: string; description?: string };
    }) =>
      api.patch<CourseVersionWithStructure>(
        `${base(courseId, versionId)}/modules/${moduleId}`,
        data
      ),
    onSuccess: onDraftUpdate,
    onError: (e: unknown) => toast.error(extractErrorMessage(e, 'Failed to update module')),
  });
}

export function useRemoveDraftModule(courseId: string, versionId: string) {
  const onDraftUpdate = useInvalidateDraft(courseId);
  return useMutation({
    mutationFn: (moduleId: string) =>
      api.del<CourseVersionWithStructure>(`${base(courseId, versionId)}/modules/${moduleId}`),
    onSuccess: onDraftUpdate,
    onError: (e: unknown) => toast.error(extractErrorMessage(e, 'Failed to remove module')),
  });
}

export function useReorderDraftModules(courseId: string, versionId: string) {
  const qc = useQueryClient();
  const onDraftUpdate = useInvalidateDraft(courseId);
  return useMutation({
    mutationFn: (orderedIds: string[]) =>
      api.patch<CourseVersionWithStructure>(`${base(courseId, versionId)}/modules/reorder`, {
        orderedIds,
      }),
    onMutate: async (orderedIds) => {
      const snapshot = await snapshotDraft(qc, courseId);
      qc.setQueryData<CourseVersionWithStructure>(courseVersionKeys.active(courseId), (old) => {
        if (!old?.modules) return old;
        return { ...old, modules: reorderByIds(old.modules, orderedIds) };
      });
      return snapshot;
    },
    onSuccess: onDraftUpdate,
    onError: (error: unknown, _vars, context) => {
      rollbackDraft(qc, courseId, context);
      toast.error(extractErrorMessage(error, 'Failed to reorder modules'));
    },
    onSettled: () => settleDraft(qc, courseId),
  });
}

// ─── Topics ─────────────────────────────────────────────────────────────────

export function useAddDraftTopic(courseId: string, versionId: string) {
  const onDraftUpdate = useInvalidateDraft(courseId);
  return useMutation({
    mutationFn: ({
      moduleId,
      data,
    }: {
      moduleId: string;
      data: { title: string; description?: string };
    }) =>
      api.post<CourseVersionWithStructure>(
        `${base(courseId, versionId)}/modules/${moduleId}/topics`,
        data
      ),
    onSuccess: onDraftUpdate,
    onError: (e: unknown) => toast.error(extractErrorMessage(e, 'Failed to add topic')),
  });
}

export function useUpdateDraftTopic(courseId: string, versionId: string) {
  const onDraftUpdate = useInvalidateDraft(courseId);
  return useMutation({
    mutationFn: ({
      topicId,
      data,
    }: {
      topicId: string;
      data: { title?: string; description?: string };
    }) =>
      api.patch<CourseVersionWithStructure>(`${base(courseId, versionId)}/topics/${topicId}`, data),
    onSuccess: onDraftUpdate,
    onError: (e: unknown) => toast.error(extractErrorMessage(e, 'Failed to update topic')),
  });
}

export function useRemoveDraftTopic(courseId: string, versionId: string) {
  const onDraftUpdate = useInvalidateDraft(courseId);
  return useMutation({
    mutationFn: (topicId: string) =>
      api.del<CourseVersionWithStructure>(`${base(courseId, versionId)}/topics/${topicId}`),
    onSuccess: onDraftUpdate,
    onError: (e: unknown) => toast.error(extractErrorMessage(e, 'Failed to remove topic')),
  });
}

export function useReorderDraftTopics(courseId: string, versionId: string) {
  const qc = useQueryClient();
  const onDraftUpdate = useInvalidateDraft(courseId);
  return useMutation({
    mutationFn: ({ moduleId, orderedIds }: { moduleId: string; orderedIds: string[] }) =>
      api.patch<CourseVersionWithStructure>(
        `${base(courseId, versionId)}/modules/${moduleId}/topics/reorder`,
        { orderedIds }
      ),
    onMutate: async ({ moduleId, orderedIds }) => {
      const snapshot = await snapshotDraft(qc, courseId);
      qc.setQueryData<CourseVersionWithStructure>(courseVersionKeys.active(courseId), (old) => {
        if (!old?.modules) return old;
        return {
          ...old,
          modules: old.modules.map((mod) =>
            mod.id === moduleId ? { ...mod, topics: reorderByIds(mod.topics, orderedIds) } : mod
          ),
        };
      });
      return snapshot;
    },
    onSuccess: onDraftUpdate,
    onError: (error: unknown, _vars, context) => {
      rollbackDraft(qc, courseId, context);
      toast.error(extractErrorMessage(error, 'Failed to reorder topics'));
    },
    onSettled: () => settleDraft(qc, courseId),
  });
}

export function useMoveDraftTopic(courseId: string, versionId: string) {
  const qc = useQueryClient();
  const onDraftUpdate = useInvalidateDraft(courseId);
  return useMutation({
    mutationFn: ({
      topicId,
      targetModuleId,
      position,
    }: {
      topicId: string;
      targetModuleId: string;
      position: number;
    }) =>
      api.patch<CourseVersionWithStructure>(`${base(courseId, versionId)}/topics/${topicId}/move`, {
        targetModuleId,
        position,
      }),
    onMutate: async ({ topicId, targetModuleId, position }) => {
      const snapshot = await snapshotDraft(qc, courseId);
      qc.setQueryData<CourseVersionWithStructure>(courseVersionKeys.active(courseId), (old) => {
        if (!old?.modules) return old;
        const result = extractTopic(old.modules, topicId);
        if (!result) return old;
        const [topic, modules] = result;
        return {
          ...old,
          modules: modules.map((mod) => {
            if (mod.id !== targetModuleId) return mod;
            const topics = [...mod.topics];
            topics.splice(position, 0, topic);
            return { ...mod, topics };
          }),
        };
      });
      return snapshot;
    },
    onSuccess: onDraftUpdate,
    onError: (error: unknown, _vars, context) => {
      rollbackDraft(qc, courseId, context);
      toast.error(extractErrorMessage(error, 'Failed to move topic'));
    },
    onSettled: () => settleDraft(qc, courseId),
  });
}

// ─── Lessons ────────────────────────────────────────────────────────────────

export function useAddDraftLesson(courseId: string, versionId: string) {
  const onDraftUpdate = useInvalidateDraft(courseId);
  return useMutation({
    mutationFn: ({
      topicId,
      data,
    }: {
      topicId: string;
      data?: { sourceLessonId?: string; title?: string };
    }) =>
      api.post<CourseVersionWithStructure>(
        `${base(courseId, versionId)}/topics/${topicId}/lessons`,
        data ?? {}
      ),
    onSuccess: onDraftUpdate,
    onError: (e: unknown) => toast.error(extractErrorMessage(e, 'Failed to add lesson')),
  });
}

export function useRemoveDraftLesson(courseId: string, versionId: string) {
  const onDraftUpdate = useInvalidateDraft(courseId);
  return useMutation({
    mutationFn: (lessonId: string) =>
      api.del<CourseVersionWithStructure>(`${base(courseId, versionId)}/lessons/${lessonId}`),
    onSuccess: onDraftUpdate,
    onError: (e: unknown) => toast.error(extractErrorMessage(e, 'Failed to remove lesson')),
  });
}

export function useReorderDraftLessons(courseId: string, versionId: string) {
  const qc = useQueryClient();
  const onDraftUpdate = useInvalidateDraft(courseId);
  return useMutation({
    mutationFn: ({ topicId, orderedIds }: { topicId: string; orderedIds: string[] }) =>
      api.patch<CourseVersionWithStructure>(
        `${base(courseId, versionId)}/topics/${topicId}/lessons/reorder`,
        { orderedIds }
      ),
    onMutate: async ({ topicId, orderedIds }) => {
      const snapshot = await snapshotDraft(qc, courseId);
      qc.setQueryData<CourseVersionWithStructure>(courseVersionKeys.active(courseId), (old) => {
        if (!old?.modules) return old;
        return {
          ...old,
          modules: old.modules.map((mod) => ({
            ...mod,
            topics: mod.topics.map((topic) =>
              topic.id === topicId
                ? { ...topic, lessons: reorderByIds(topic.lessons, orderedIds) }
                : topic
            ),
          })),
        };
      });
      return snapshot;
    },
    onSuccess: onDraftUpdate,
    onError: (error: unknown, _vars, context) => {
      rollbackDraft(qc, courseId, context);
      toast.error(extractErrorMessage(error, 'Failed to reorder lessons'));
    },
    onSettled: () => settleDraft(qc, courseId),
  });
}

export function useMoveDraftLesson(courseId: string, versionId: string) {
  const qc = useQueryClient();
  const onDraftUpdate = useInvalidateDraft(courseId);
  return useMutation({
    mutationFn: ({
      lessonId,
      targetTopicId,
      position,
    }: {
      lessonId: string;
      targetTopicId: string;
      position: number;
    }) =>
      api.patch<CourseVersionWithStructure>(
        `${base(courseId, versionId)}/lessons/${lessonId}/move`,
        { targetTopicId, position }
      ),
    onMutate: async ({ lessonId, targetTopicId, position }) => {
      const snapshot = await snapshotDraft(qc, courseId);
      qc.setQueryData<CourseVersionWithStructure>(courseVersionKeys.active(courseId), (old) => {
        if (!old?.modules) return old;
        const result = extractLesson(old.modules, lessonId);
        if (!result) return old;
        const [lesson, modules] = result;
        return {
          ...old,
          modules: modules.map((mod) => ({
            ...mod,
            topics: mod.topics.map((topic) => {
              if (topic.id !== targetTopicId) return topic;
              const lessons = [...topic.lessons];
              lessons.splice(position, 0, lesson);
              return { ...topic, lessons };
            }),
          })),
        };
      });
      return snapshot;
    },
    onSuccess: onDraftUpdate,
    onError: (error: unknown, _vars, context) => {
      rollbackDraft(qc, courseId, context);
      toast.error(extractErrorMessage(error, 'Failed to move lesson'));
    },
    onSettled: () => settleDraft(qc, courseId),
  });
}

// ─── Metadata ──────────────────────────────────────────────────────────────

export function useUpdateDraftMetadata(courseId: string, versionId: string | undefined) {
  const onDraftUpdate = useInvalidateDraft(courseId);
  return useMutation({
    mutationFn: (
      data: Partial<{
        title: string;
        description: string | null;
        iconUrl: string | null;
        headerImageUrl: string | null;
        accentColour: string | null;
        progressionType: 'lesson' | 'topic' | 'module';
      }>
    ) => {
      if (!versionId) throw new Error('No active draft');
      return api.patch<CourseVersionWithStructure>(`${base(courseId, versionId)}/metadata`, data);
    },
    onSuccess: onDraftUpdate,
    onError: (e: unknown) => toast.error(extractErrorMessage(e, 'Failed to update draft metadata')),
  });
}
